/**
 * The builder: a full-body wardrobe and a figure that changes as you touch it.
 *
 * Every tile is the whole operator with one part swapped, not a picture of the
 * part on its own. The renderers are memoised, and each tile answers the question a learner
 * has, which is not "what does this hood look like" but "what do *I* look like
 * in it". A grid of disembodied haircuts is a catalogue; this is a mirror.
 *
 * One rail at a time. Eighty-one tiles at once is a wall, and the rails are
 * separate decisions: groups contain focused tabs, and the grid is the
 * one in hand, and Tab moves through what is on screen rather than through
 * everything that exists.
 *
 * ## Locks
 *
 * A few parts open as levels are cleared, and the ones that do are deliberate:
 * garments, headwear, held tools, footwear and character gestures — kit that reads as
 * having been somewhere. Nothing that makes a person look like themselves is
 * ever locked, which is the rule the design system was drawn to and the reason
 * this screen can gate anything at all without being unkind. A locked tile is
 * shown and can be tried on. A preview cannot be saved until the changed
 * parts are open. A previously worn reward survives resetting the record.
 *
 * The gate is the tab's, not the server's, for the reason docs/cheating.md
 * gives about the whole record: a learner who edits their own storage to wear
 * a visor early has cheated themselves out of a small pleasure and nobody else
 * out of anything.
 */
import { useCallback, useMemo, useState } from 'react';
import { useReducedMotion } from 'motion/react';
import {
  CATALOGUE,
  isPartUnlocked,
  lockedChanges,
  plateOf,
  parseSeed,
  randomSelection,
  sameSelection,
  toSeed,
  type RailId,
  type Selection,
} from '../../lib/avatar';
import { useLocale, useStrings } from '../../state/useLocale';
import { avatarWords } from '../../lib/strings/avatar-rewards';
import { characterWords } from '../../lib/strings/avatar-character';
import { CHARACTER_LOOKS, characterLook, type LookId } from '../../lib/avatar-looks';
import { GARMENT_PALETTES } from '../../lib/avatar-items';
import { sound } from '../../lib/sound';
import { Button } from '../../ui';
import { Character, Operator, OperatorPlate } from './Operator';
import { AvatarRewards } from './Rewards';

export interface BuilderProps {
  /** The figure to open on: what they are wearing now. */
  seed: string;
  /** The figure their id gives them, which "start again" goes back to. */
  derived: string;
  /** Levels cleared, which is what opens the locked parts. */
  level: number;
  busy: boolean;
  /** Null is "wear the one my id gives me". Answers false when the account refused it. */
  onSave(seed: string | null): void;
  onCancel(): void;
}

/** The two sizes a figure is actually seen at in a room: a message, and a member row.
    The profile's own size is the plate above them, so it is not repeated here. */
const PREVIEWS = [16, 32];
const GROUPS = {
  outfit: ['kit', 'color', 'boots'], gear: ['hat', 'acc', 'prop'],
  face: ['build', 'hair', 'eyes', 'face', 'mouth', 'tone'], life: ['pose', 'plate'],
} satisfies Record<string, RailId[]>;

export function Builder({ seed, derived, level, busy, onSave, onCancel }: BuilderProps) {
  const s = useStrings().profile;
  const { locale, dir } = useLocale();
  const rewards = avatarWords[locale];
  const c = characterWords[locale];
  const reduced = useReducedMotion();
  const [sel, setSel] = useState<Selection>(() => parseSeed(seed));
  const [rail, setRail] = useState<RailId>('kit');
  const [animate, setAnimate] = useState(true);
  const [replay, setReplay] = useState(0);

  const worn = useMemo(() => parseSeed(seed), [seed]);
  const base = useMemo(() => parseSeed(derived), [derived]);
  const dirty = !sameSelection(sel, worn);
  const locked = lockedChanges(sel, worn, level);
  const animated = sel.pose > 0 || ['lift', 'nod', 'spark'].includes(plateOf(sel));
  const group = (Object.keys(GROUPS) as Array<keyof typeof GROUPS>).find((id) => (GROUPS[id] as RailId[]).includes(rail)) ?? 'outfit';
  const tabs = CATALOGUE.filter((r) => (GROUPS[group] as RailId[]).includes(r.id));
  const current = CATALOGUE.find((r) => r.id === rail) ?? CATALOGUE[0]!;
  const open = current.parts.filter((p) => isPartUnlocked(current.id, p.index, level)).length;

  const pick = useCallback((id: RailId, index: number) => {
    sound.click();
    setSel((prev) => ({ ...prev, [id]: index }));
  }, []);

  const surprise = useCallback(() => {
    sound.click();
    setSel(randomSelection(level));
  }, [level]);

  const save = useCallback(() => {
    if (busy || !dirty || lockedChanges(sel, worn, level).length > 0) return;
    sound.click();
    // Null rather than the seed when it is the one their id gives them: that
    // is not a figure they built, and storing it would freeze a default that
    // is meant to follow the id.
    onSave(sameSelection(sel, base) ? null : toSeed(sel));
  }, [sel, base, onSave, busy, dirty, worn, level]);

  return (
    <div className="builder-workshop">
    <div className="builder">
      <div className="builder-preview">
        <div className="character-stage"><OperatorPlate key={`${sel.plate}-${sel.pose}-${replay}`} seed={sel} size={192} alt={s.operator.label} motion={animate && !reduced} /></div>
        <div className="character-equipped" aria-label={c.equipped}>
          {(['kit', 'hat', 'prop', 'boots', 'color', 'pose'] as RailId[]).map((id) => {
            const part = CATALOGUE.find((r) => r.id === id)!.parts[sel[id] ?? 0]!;
            const name = s.parts[id]?.[part.key] ?? part.label;
            return <button type="button" className="character-equipped-item" key={id} disabled={busy} onClick={() => setRail(id)}><span className="label">{s.rails[id]}</span><strong>{name}</strong></button>;
          })}
        </div>
        <label className="builder-motion note">
          <input type="checkbox" checked={animate && !reduced} disabled={busy || !!reduced} onChange={(e) => setAnimate(e.target.checked)} />
          {rewards.motion}
        </label>
        {animated && <Button variant="quiet" disabled={busy || !animate || !!reduced} onClick={() => setReplay((n) => n + 1)}>{rewards.replay}</Button>}
        <p className="note builder-motion-note">{rewards.motionWhy}</p>
        <div className="builder-sizes" aria-hidden="true">
          {PREVIEWS.map((px) => (
            <span className="builder-size" key={px}>
              <Operator seed={sel} size={px} />
              <span className="note">{s.build.atSize(px)}</span>
            </span>
          ))}
        </div>
        <div className="builder-actions">
          <Button variant="solid" onClick={save} disabled={busy || !dirty || locked.length > 0}>
            {busy ? s.build.saving : s.build.save}
          </Button>
          <Button variant="quiet" onClick={surprise} disabled={busy}>
            {s.build.random}
          </Button>
          <Button variant="quiet" onClick={() => setSel(base)} disabled={busy || sameSelection(sel, base)}>
            {s.build.reset}
          </Button>
          <Button variant="quiet" onClick={onCancel} disabled={busy}>
            {dirty ? s.build.cancel : s.operator.close}
          </Button>
        </div>
        <p className="note builder-status" role="status" aria-live="polite">
          {locked.length > 0 ? `${rewards.previewOnly} ${s.build.locked(Math.max(...locked.map((id) => CATALOGUE.find((r) => r.id === id)!.parts[sel[id]]!.level)))}` : dirty ? s.build.unsaved : ''}
        </p>
        {dirty && <Button variant="quiet" disabled={busy} onClick={() => setSel(worn)}>{rewards.restore}</Button>}
      </div>

      <div className="builder-rails">
        <div className="character-looks">
          <p className="label">{c.looks}</p><p className="note">{c.lookWhy}</p>
          <div className="character-look-grid">
            {(Object.keys(CHARACTER_LOOKS) as LookId[]).map((id) => {
              const look = characterLook(sel, id);
              const lockedLook = lockedChanges(look, worn, level);
              return <button type="button" className="character-look" disabled={busy} key={id} onClick={() => { sound.click(); setSel(look); setRail('kit'); }}>
                <Character seed={look} size={64} motion={false} />
                <strong>{c.presets[id]}</strong>
                {lockedLook.length > 0 && <span className="note">{s.build.locked(Math.max(...lockedLook.map((rail) => CATALOGUE.find((r) => r.id === rail)!.parts[look[rail]]!.level)))}</span>}
              </button>;
            })}
          </div>
        </div>
        <div className="character-groups" role="group" aria-label={s.build.title}>
          {(Object.keys(GROUPS) as Array<keyof typeof GROUPS>).map((id) => <button type="button" key={id} disabled={busy} aria-pressed={id === group} onClick={() => { sound.click(); setRail(GROUPS[id][0]!); }}>{c.groups[id]}</button>)}
        </div>
        <div className="rail-tabs" role="tablist" aria-label={s.build.title}>
          {tabs.map((r) => (
            <button
              key={r.id}
              type="button"
              role="tab"
              id={`rail-tab-${r.id}`}
              aria-selected={r.id === rail}
              aria-controls={`rail-${r.id}`}
              tabIndex={r.id === rail ? 0 : -1}
              disabled={busy}
              className={r.id === rail ? 'rail-tab is-on' : 'rail-tab'}
              onKeyDown={(e) => {
                if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
                e.preventDefault();
                const at = tabs.findIndex((item) => item.id === rail);
                const step = (e.key === 'ArrowRight' ? 1 : -1) * (dir === 'rtl' ? -1 : 1);
                const index = e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1 : (at + step + tabs.length) % tabs.length;
                const next = tabs[index]!.id;
                setRail(next);
                document.getElementById(`rail-tab-${next}`)?.focus();
              }}
              onClick={() => {
                sound.click();
                setRail(r.id);
              }}
            >
              {s.rails[r.id]}
            </button>
          ))}
        </div>

        <div className="rail-panel" role="tabpanel" id={`rail-${current.id}`} aria-labelledby={`rail-tab-${current.id}`}>
          <p className="note rail-note">
            {s.build.openParts(open, current.parts.length)}
            {open < current.parts.length ? ` · ${s.build.locksWhy}` : ''}
          </p>
          <div className="part-grid" role="group" aria-label={s.build.railLabel(s.rails[current.id])}>
            {current.parts.map((part) => {
              const unlocked = isPartUnlocked(current.id, part.index, level);
              const name = s.parts[current.id]?.[part.key] ?? part.label;
              const on = sel[current.id] === part.index;
              return (
                <button
                  key={part.key}
                  type="button"
                  className={`part${on ? ' is-on' : ''}${unlocked ? '' : ' is-locked'}`}
                  aria-pressed={on}
                  disabled={busy}
                  title={unlocked ? name : `${name} — ${s.build.locked(part.level)}`}
                  onClick={() => pick(current.id, part.index)}
                >
                  {/* The plate is the frame rather than part of the figure, so
                      its tiles have to draw the frame or every one of the four
                      would be the same picture. */}
                  {current.id === 'plate' ? (
                    <OperatorPlate seed={{ ...sel, plate: part.index }} size={48} className="part-plate" motion={false} />
                  ) : ['hair', 'eyes', 'face', 'mouth', 'tone', 'acc'].includes(current.id) ? (
                    <Operator seed={{ ...sel, [current.id]: part.index }} size={48} />
                  ) : (
                    <Character seed={{ ...sel, [current.id]: part.index }} size={64} motion={false} />
                  )}
                  {current.id === 'color' && <span className="character-swatch" style={{ backgroundColor: GARMENT_PALETTES[part.index]?.G }} />}
                  <span className="part-name">{name}</span>
                  {!unlocked && <span className="part-lock">{s.build.locked(part.level)}</span>}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
    <AvatarRewards level={level} selection={sel} busy={busy} onPick={(reward) => { pick(reward.rail, reward.index); setRail(reward.rail); }} />
    </div>
  );
}

export default Builder;
